import { Controller } from '@hotwired/stimulus';

/**
 * ```ts
 * <div data-controller="field" class="form-field">
 *   <input name="addresses[0].city"
 *     data-field-target="input"
 *     data-action="blur->field#validate" />
 *   <div data-field-target="error"></div>
 * </div>
 * ```
 */
class FieldController extends Controller {
    constructor() {
        super(...arguments);
        this.touched = false;
    }
    errorTargetConnected(element) {
        var _a;
        /* istanbul ignore next */
        this.touched = !!((_a = element.textContent) === null || _a === void 0 ? void 0 : _a.trim());
    }
    errorTargetDisconnected() {
        this.touched = false;
    }
    /**
     * Called by ZodFormController when field outlet is connected or disconnected
     */
    setValidator(validator) {
        this.validator = validator;
    }
    validate() {
        if (this.validator != null) {
            this.setTouched();
            this.validator.validate();
        }
    }
    setTouched() {
        this.touched = true;
    }
    getName() {
        if (this.hasInputTarget)
            return this.inputTarget.getAttribute("name");
    }
    setError(message) {
        if (this.touched && this.hasErrorTarget)
            this.errorTarget.textContent = message;
    }
    clearError() {
        if (this.hasErrorTarget)
            this.errorTarget.textContent = "";
    }
}
FieldController.targets = ["input", "error"];

const isIndex = (key) => !isNaN(parseInt(key));
/**
 * Tokenizes addresses[0].city to ['addresses', '0', 'city']
 */
function tokenize(path) {
    return path
        .trim()
        .replace(/\[(\w+)\]/g, ".$1") // [foo] to .foo
        .replace(/^\.|\.$/, "") // trim('.')
        .split(".");
}
function getPath(nameOrPath) {
    const tokens = typeof nameOrPath === "string" ? tokenize(nameOrPath) : nameOrPath;
    return tokens.join(".");
}
/**
 * Deeply sets value for a path (target, 'addresses[0].city', 'Springfield')
 */
function set(target, path, value) {
    const tokens = tokenize(path);
    tokens.forEach((cur, i) => {
        const next = tokens[i + 1];
        if (next == null) {
            target[cur] = value;
        }
        else {
            target = target[cur] = target[cur] || (isIndex(next) ? [] : {});
        }
    });
}
class ZodFormController extends Controller {
    constructor(context, schema) {
        super(context);
        this.schema = schema;
        this.fields = new Map();
    }
    fieldOutletConnected(field) {
        const name = field.getName();
        if (name) {
            this.fields.set(getPath(name), field);
            field.setValidator(this);
        }
    }
    fieldOutletDisconnected(field) {
        const name = field.getName();
        if (name) {
            this.fields.delete(getPath(name));
            field.setValidator(undefined);
        }
    }
    submit(event) {
        const { success } = this.validateSchema(true);
        if (!success) {
            event.preventDefault();
        }
        return success;
    }
    validate() {
        const { success } = this.validateSchema(false);
        return success;
    }
    validateSchema(isFormSubmitting) {
        if (isFormSubmitting)
            this.fields.forEach((field) => field.setTouched());
        const data = {};
        for (const [path, value] of new FormData(this.element).entries()) {
            set(data, path, value);
        }
        const result = this.schema.safeParse(data);
        if (result.success) {
            this.clearErrors();
            return { success: true, data: result.data };
        }
        else {
            // translate messages manually - custom error map message override does not work yet
            // https://github.com/colinhacks/zod/issues/2940
            const issues = result.error.issues.map((issue) => {
                var _a;
                return (Object.assign(Object.assign({}, issue), { message: (_a = this.errorMapValue[issue.message]) !== null && _a !== void 0 ? _a : issue.message }));
            });
            this.setErrors(issues);
            return { success: false, issues };
        }
    }
    clearErrors() {
        if (this.hasErrorTarget)
            this.errorTarget.textContent = "";
        this.fields.forEach((field) => field.clearError());
    }
    setErrors(issues) {
        const errors = new Map(); // Map<path, message>
        errors.set("", ""); // top level form error
        issues.forEach((issue) => errors.set(getPath(issue.path), issue.message));
        if (this.hasErrorTarget)
            this.errorTarget.textContent = errors.get("");
        this.fields.forEach((field, path) => {
            const message = errors.get(path);
            if (message) {
                field.setError(message);
            }
            else {
                field.clearError();
            }
        });
    }
}
ZodFormController.values = { errorMap: Object };
ZodFormController.outlets = ["field"];
ZodFormController.targets = ["error"];
/**
 * ```ts
 * const schema = z.object({
 *   email: z.string().email("invalid_email"),
 *   addresses: z.array(z.object({ city: z.string() }))
 * })
 *
 * <form data-controller="form"
 *   data-form-field-outlet=".form-field"
 *   data-action="form#submit"
 *   data-form-error-map-value='{ "invalid_email": "correo inválido", ... }'>
 *
 *   <div data-form-target="error"></div>
 *
 *   <div data-controller="field" class="form-field">
 *    <input name="addresses[0].city"
 *      data-field-target="input"
 *      data-action="blur->field#validate" />
 *    <div data-field-target="error"></div>
 *   </div>
 * </form>
 * ```
 */
function FormController(schema) {
    return class extends ZodFormController {
        constructor(context) {
            super(context, schema);
        }
    };
}

export { FieldController, FormController };
